import { AxiosError } from "axios";
import { HttpService } from "data/http-service";
import {
  CreatePersonalStep,
  CreateSeance,
  InsertSeanceTechnique,
  Seance,
  UpdateSeanceTechnique,
} from "domain/entities/seances.entities";
import { translate } from "ui/i18n";
import { Snackbar } from "ui/pages/snackbar";
import {
  seanceDtoSchema,
  seancesDtoSchema,
} from "data/seances/seances.dto.schemas";
import { SeancesGateway } from "domain/seances/seances.gateway";
import {
  ActivitiesPageFilters,
  ActivityAdvice,
  ActivityMateriel,
  DurationFilter,
} from "domain/entities/activities.entities";
import { getActivitiesFilters } from "data/activities/get-activities-filters";
import {
  adviceDtoSchema,
  materielsDtoSchema,
} from "data/activities/activities.dto.schemas";
import { SeanceTechnique } from "domain/entities/techniques.entities";
import { seanceTechniquesDtoSchema } from "data/techniques/techniques.dto.schemas";

export class SeancesHttpGateway implements SeancesGateway {
  async getSeances(
    filters: Partial<ActivitiesPageFilters> &
      Partial<{ duration: DurationFilter }>
  ): Promise<{
    seances: Seance[];
    totalCount: number;
    success: boolean;
  }> {
    return HttpService.get("seances", { params: getActivitiesFilters(filters) })
      .then((response) => {
        if (response.status === 200) {
          const parsedSeances = seancesDtoSchema.safeParse(response.data.data);
          if (parsedSeances.success) {
            return {
              seances: parsedSeances.data,
              totalCount: response.data.total,
              success: true,
            };
          } else return { seances: [], totalCount: 0, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seances.FindError"),
          });
          return {
            seances: [],
            totalCount: 0,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seances.FindError"),
        });
        return { seances: [] as Seance[], totalCount: 0, success: false };
      });
  }

  async getSeance(id: number): Promise<{
    seance: Seance;
    success: boolean;
  }> {
    return HttpService.get(`seances/${id}`)
      .then((response) => {
        if (response.status === 200) {
          const parsedSeance = seanceDtoSchema.safeParse(response.data);
          if (parsedSeance.success) {
            return {
              seance: {
                ...parsedSeance.data,
                techniques: parsedSeance.data.techniques?.flat(),
              },
              success: true,
            };
          } else return { seance: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.FindError"),
          });
          return {
            seance: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.FindError"),
        });
        return { seance: null as Seance, success: false };
      });
  }

  async getSeanceAdvice(id: number): Promise<{
    advice: ActivityAdvice | null;
    success: boolean;
  }> {
    return HttpService.get(`conseils/random`, { params: { seanceId: id } })
      .then((response) => {
        if (response.status === 200) {
          const parsedAdvice = adviceDtoSchema.safeParse(response.data);
          if (parsedAdvice.success) {
            return {
              advice: parsedAdvice.data,
              success: true,
            };
          } else return { advice: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Advice.FindError"),
          });
          return {
            advice: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Advice.FindError"),
        });
        return { advice: null as ActivityAdvice, success: false };
      });
  }

  async createSeance(seance: CreateSeance): Promise<{
    success: boolean;
    id: number | null;
  }> {
    return HttpService.post(`seances`, seance)
      .then((response) => {
        if (response.status === 201 && response.data.id) {
          return {
            id: response.data.id,
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.CreationError"),
          });
          return {
            id: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.CreationError"),
        });
        return { id: null as number, success: false };
      });
  }

  async updateSeance({
    seance,
    id,
  }: {
    seance: CreateSeance;
    id: number;
  }): Promise<{
    success: boolean;
    id: number | null;
  }> {
    return HttpService.patch(`seances/${id}`, seance)
      .then((response) => {
        if (response.status === 200 && response.data.id) {
          return {
            id: response.data.id,
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.UpdateError"),
          });
          return {
            id: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.UpdateError"),
        });
        return { id: null as number, success: false };
      });
  }

  async createPersonalStep(technique: CreatePersonalStep): Promise<{
    success: boolean;
    techniqueId: number | null;
  }> {
    return HttpService.post(`user-techniques`, technique)
      .then((response) => {
        if (response.status === 201 && response.data.id) {
          return {
            techniqueId: response.data.id,
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.PersonalStepCreationError"),
          });
          return {
            techniqueId: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.PersonalStepCreationError"),
        });
        return { techniqueId: null as number, success: false };
      });
  }

  async updatePersonalStep(body: {
    id: number;
    technique: CreatePersonalStep;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.patch(`user-techniques/${body.id}`, body.technique)
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.PersonalStepUpdateError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.PersonalStepUpdateError"),
        });
        return { success: false };
      });
  }

  async bulkInsertTechniques({
    techniques,
    seanceId,
  }: {
    techniques: InsertSeanceTechnique[];
    seanceId: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.post(`seances/${seanceId}/techniques`, techniques, {
      additionalHeaders: { "X-Service-Method": "batchInsert" },
    })
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.BulkInsertTechniquesError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.BulkInsertTechniquesError"),
        });
        return { success: false };
      });
  }

  async insertTechnique({
    technique,
    seanceId,
  }: {
    technique: InsertSeanceTechnique;
    seanceId: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.post(`seances/${seanceId}/techniques`, technique)
      .then((response) => {
        if (response.status === 201) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.BulkInsertTechniqueError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.BulkInsertTechniqueError"),
        });
        return { success: false };
      });
  }

  async bulkUpdateTechniques({
    techniques,
    seanceId,
  }: {
    techniques: UpdateSeanceTechnique[];
    seanceId: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.post(`seances/${seanceId}/techniques`, techniques, {
      additionalHeaders: { "X-Service-Method": "batchUpdate" },
    })
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.BulkUpdateTechniquesError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.BulkUpdateTechniquesError"),
        });
        return { success: false };
      });
  }

  async setSeanceActive({
    seanceId,
    duration,
  }: {
    seanceId: number;
    duration: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.patch(`seances/${seanceId}`, { active: true, duration })
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.UpdateError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.UpdateError"),
        });
        return { success: false };
      });
  }

  async deleteSeanceTechnique({
    seanceId,
    techniqueId,
  }: {
    seanceId: number;
    techniqueId: number;
  }): Promise<{
    success: boolean;
  }> {
    return HttpService.delete(`seances/${seanceId}/techniques/${techniqueId}`)
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.DeleteTechniquesError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.DeleteTechniquesError"),
        });
        return { success: false };
      });
  }

  async getSeanceTechniques(seanceId: number): Promise<{
    techniques: SeanceTechnique[];
    success: boolean;
  }> {
    return HttpService.get(`seances/${seanceId}/techniques`)
      .then((response) => {
        if (response.status === 200) {
          const parsedTechniques = seanceTechniquesDtoSchema.safeParse(
            response.data.data
          );
          if (parsedTechniques.success) {
            return {
              techniques: parsedTechniques.data,
              success: true,
            };
          } else return { techniques: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:CreateSeance.GetTechniquesError"),
          });
          return {
            techniques: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:CreateSeance.GetTechniquesError"),
        });
        return { techniques: [] as SeanceTechnique[], success: false };
      });
  }

  async getSeanceMateriels(id: number): Promise<{
    materiels: ActivityMateriel[];
    success: boolean;
  }> {
    return HttpService.get(`seances/${id}/materiels`)
      .then((response) => {
        if (response.status === 200) {
          const parsedMateriels = materielsDtoSchema.safeParse(response.data);
          if (parsedMateriels.success) {
            return {
              materiels: parsedMateriels.data,
              success: true,
            };
          } else return { materiels: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Materiels.FindError"),
          });
          return {
            materiels: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Materiels.FindError"),
        });
        return { materiels: [] as ActivityMateriel[], success: false };
      });
  }

  async deleteSeance(seanceId: number): Promise<{
    success: boolean;
  }> {
    return HttpService.delete(`seances/${seanceId}`)
      .then((response) => {
        if (response.status === 200) {
          return { success: true };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.DeleteError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.DeleteError"),
        });
        return { success: false };
      });
  }

  async duplicateSeance(seanceId: number): Promise<{
    success: boolean;
    seanceId: number | null;
  }> {
    return HttpService.post(`seances/duplicate`, { seanceId })
      .then((response) => {
        if (response.status === 201) {
          return { success: true, seanceId: response.data.id };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.DuplicateError"),
          });
          return {
            success: false,
            seanceId: null as number | null,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.DuplicateError"),
        });
        return { success: false, seanceId: null as number | null };
      });
  }

  async getFavoritesSeances(seanceIds: number[]): Promise<{
    seances: Seance[];
    success: boolean;
  }> {
    const ids = seanceIds.length > 0 ? seanceIds : [-1];
    return HttpService.get("seances", { params: { "id[$in]": ids } })
      .then((response) => {
        if (response.status === 200) {
          const parsedSeances = seancesDtoSchema.safeParse(response.data.data);
          if (parsedSeances.success) {
            return {
              seances: parsedSeances.data,
              success: true,
            };
          } else return { seances: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seances.FindError"),
          });
          return {
            seances: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seances.FindError"),
        });
        return { seances: [] as Seance[], success: false };
      });
  }

  async getUserSeanceImages(seanceId: number): Promise<{
    success: boolean;
    url: string | null;
  }> {
    return HttpService.post(`exports/user-seance`, { seanceId })
      .then((response) => {
        if (response.status === 201) {
          return { success: true, url: response.data.exportUrl };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.ExportImagesError"),
          });
          return {
            success: false,
            url: null as string | null,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.ExportImagesError"),
        });
        return { success: false, url: null as string | null };
      });
  }

  async getRandomSeance(): Promise<{
    seance: Seance;
    success: boolean;
  }> {
    return HttpService.get("seances/random")
      .then((response) => {
        if (response.status === 200) {
          const parsedSeance = seanceDtoSchema.safeParse(response.data);
          if (parsedSeance.success) {
            return {
              seance: {
                ...parsedSeance.data,
                techniques: parsedSeance.data.techniques?.flat(),
              },
              success: true,
            };
          } else return { seance: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.FindError"),
          });
          return {
            seance: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.FindError"),
        });
        return { seance: null as Seance, success: false };
      });
  }

  async createSeanceTiming(body: {
    seanceId: number;
    timing: string;
  }): Promise<{
    success: boolean;
    timing: string;
  }> {
    return HttpService.post(`/seance-timing-ref`, body)
      .then((response) => {
        if (response.status === 201 && response.data.timing) {
          return {
            success: true,
            timing: response.data.timing,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.TimingCreationError"),
          });
          return {
            success: false,
            timing: null as string | null,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.TimingCreationError"),
        });
        return { success: false, timing: null as string | null };
      });
  }

  async removeSeanceTiming(seanceId: number): Promise<{
    success: boolean;
  }> {
    return HttpService.delete(`/seance-timing-ref?seanceId=${seanceId}`)
      .then((response) => {
        if (response.status === 200) {
          return {
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.DeleteTimingError"),
          });
          return {
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.DeleteTimingError"),
        });
        return { success: false };
      });
  }

  async updateSeanceTiming(body: {
    seanceId: number;
    timing: string;
  }): Promise<{
    success: boolean;
    timing: string;
  }> {
    return HttpService.patch(`/seance-timing-ref?seanceId=${body.seanceId}`, {
      timing: body.timing,
    })
      .then((response) => {
        if (response.status === 200 && response.data.timing) {
          return {
            success: true,
            timing: response.data.timing,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:Seance.TimingUpdateError"),
          });
          return {
            success: false,
            timing: null as string | null,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Seance.TimingUpdateError"),
        });
        return { success: false, timing: null as string | null };
      });
  }
}
